/**
 * PATH: packages/email-hub/src/feature/compose/sendHold.ts
 * FILE_ID: EMAIL-COMPOSE-LIB-SENDHOLD
 * PURPOSE: Framework-agnostic "undo send" hold — a short window during which a
 *          queued send can be cancelled before it actually fires.
 * WHY: Operators want a Gmail-style grace period to catch an "oops, wrong
 *      recipient / forgot the attachment" before the email leaves. Keeping the
 *      timer/state machine out of React makes it unit-testable with injected
 *      clocks (no jsdom timing flake) and reusable from any caller.
 * FLOW:
 *   arm(commit) ─▶ [pending, counting down] ─▶ window elapses ─▶ commit()
 *                         │
 *                         └─ cancel() ─▶ idle (commit never runs)
 *                         └─ flush()  ─▶ commit() now (e.g. on unmount)
 * DEPENDENCIES: none (timers are injectable; defaults to global timers).
 * RELATED:
 *   - useUndoSend.ts: thin React wrapper over this controller.
 *   - pages/ComposePage.tsx: arms the hold from the Send button.
 */

export const DEFAULT_UNDO_WINDOW_MS = 8000
export const UNDO_TICK_MS = 250

/**
 * Whole seconds left until a deadline, never negative. Pure I→O so the
 * countdown label is testable without a clock.
 */
export function secondsRemaining(deadlineMs: number, nowMs: number): number {
  const ms = deadlineMs - nowMs
  if (ms <= 0) return 0
  return Math.ceil(ms / 1000)
}

export interface SendHoldSnapshot {
  pending: boolean
  secondsLeft: number
}

export interface SendHoldTimers {
  now: () => number
  setTimeout: (cb: () => void, ms: number) => number
  clearTimeout: (id: number) => void
  setInterval: (cb: () => void, ms: number) => number
  clearInterval: (id: number) => void
}

type Listener = () => void

export interface SendHold {
  /** Begin the grace window. Ignored if a hold is already pending. */
  arm: (commit: () => void) => void
  /** Abort a pending hold — commit never runs. No-op when idle. */
  cancel: () => void
  /** Fire a pending hold immediately (e.g. on unmount, so nothing is lost). */
  flush: () => void
  /** Stable snapshot getter for useSyncExternalStore. */
  getSnapshot: () => SendHoldSnapshot
  /** Subscribe to state changes; returns an unsubscribe fn. */
  subscribe: (listener: Listener) => () => void
  isPending: () => boolean
}

function defaultTimers(): SendHoldTimers {
  return {
    now: () => Date.now(),
    setTimeout: (cb, ms) => globalThis.setTimeout(cb, ms) as unknown as number,
    clearTimeout: (id) => globalThis.clearTimeout(id),
    setInterval: (cb, ms) => globalThis.setInterval(cb, ms) as unknown as number,
    clearInterval: (id) => globalThis.clearInterval(id),
  }
}

/**
 * Create a send-hold controller. Timers are injectable so tests can drive the
 * countdown deterministically; production uses the global clock.
 */
export function createSendHold(
  windowMs: number = DEFAULT_UNDO_WINDOW_MS,
  timers?: Partial<SendHoldTimers>,
): SendHold {
  const T: SendHoldTimers = { ...defaultTimers(), ...timers }

  let pending = false
  let secondsLeft = 0
  let commit: (() => void) | null = null
  let timerId: number | null = null
  let intervalId: number | null = null
  const listeners = new Set<Listener>()
  // Cached so getSnapshot returns a stable reference between emits — required
  // by useSyncExternalStore to avoid infinite render loops.
  let snapshot: SendHoldSnapshot = { pending, secondsLeft }

  const emit = () => {
    // Skip no-op emits (e.g. sub-second interval ticks where the whole-second
    // count is unchanged) so subscribers/React don't re-render needlessly and
    // getSnapshot keeps a stable reference for useSyncExternalStore.
    if (snapshot.pending === pending && snapshot.secondsLeft === secondsLeft) return
    snapshot = { pending, secondsLeft }
    listeners.forEach((l) => l())
  }

  const clearTimers = () => {
    if (timerId != null) {
      T.clearTimeout(timerId)
      timerId = null
    }
    if (intervalId != null) {
      T.clearInterval(intervalId)
      intervalId = null
    }
  }

  const reset = () => {
    pending = false
    secondsLeft = 0
    commit = null
    clearTimers()
  }

  const fire = () => {
    const fn = commit
    reset()
    emit()
    // Run the commit AFTER state is cleared/emitted so a re-arm from inside
    // the commit (unlikely) sees an idle controller.
    fn?.()
  }

  const arm: SendHold['arm'] = (commitFn) => {
    if (pending) return
    commit = commitFn
    pending = true
    const deadline = T.now() + windowMs
    secondsLeft = secondsRemaining(deadline, T.now())
    timerId = T.setTimeout(fire, windowMs)
    intervalId = T.setInterval(() => {
      secondsLeft = secondsRemaining(deadline, T.now())
      emit()
    }, UNDO_TICK_MS)
    emit()
  }

  const cancel: SendHold['cancel'] = () => {
    if (!pending) return
    reset()
    emit()
  }

  const flush: SendHold['flush'] = () => {
    if (!pending) return
    fire()
  }

  return {
    arm,
    cancel,
    flush,
    getSnapshot: () => snapshot,
    subscribe: (listener) => {
      listeners.add(listener)
      return () => {
        listeners.delete(listener)
      }
    },
    isPending: () => pending,
  }
}
